import { useRef } from 'react'
import type { CropRect } from '../services/ocr'

type Handle = 'nw' | 'ne' | 'sw' | 'se' | 'move'

const MIN = 0.1
const clamp = (v: number, lo: number, hi: number) => Math.min(Math.max(v, lo), hi)

/** A simple crop rectangle with four large corner handles, drawn over the photo. */
export function CropBox({ rect, onChange }: { rect: CropRect; onChange: (rect: CropRect) => void }) {
  const areaRef = useRef<HTMLDivElement>(null)
  const drag = useRef<{ handle: Handle; startX: number; startY: number; start: CropRect } | null>(null)

  function begin(handle: Handle, event: React.PointerEvent) {
    event.preventDefault()
    event.stopPropagation()
    ;(event.target as Element).setPointerCapture(event.pointerId)
    drag.current = { handle, startX: event.clientX, startY: event.clientY, start: rect }
  }

  function move(event: React.PointerEvent) {
    const state = drag.current
    const area = areaRef.current?.getBoundingClientRect()
    if (!state || !area) return
    const dx = (event.clientX - state.startX) / area.width
    const dy = (event.clientY - state.startY) / area.height
    const s = state.start
    let { x, y, w, h } = s
    if (state.handle === 'move') {
      x = clamp(s.x + dx, 0, 1 - s.w)
      y = clamp(s.y + dy, 0, 1 - s.h)
    } else {
      if (state.handle.includes('w')) {
        x = clamp(s.x + dx, 0, s.x + s.w - MIN)
        w = s.w + (s.x - x)
      }
      if (state.handle.includes('e')) w = clamp(s.w + dx, MIN, 1 - s.x)
      if (state.handle.includes('n')) {
        y = clamp(s.y + dy, 0, s.y + s.h - MIN)
        h = s.h + (s.y - y)
      }
      if (state.handle.includes('s')) h = clamp(s.h + dy, MIN, 1 - s.y)
    }
    onChange({ x, y, w, h })
  }

  const end = () => {
    drag.current = null
  }

  return (
    <div className="crop-area" ref={areaRef} onPointerMove={move} onPointerUp={end} onPointerCancel={end}>
      <div
        className="crop-rect"
        style={{ left: `${rect.x * 100}%`, top: `${rect.y * 100}%`, width: `${rect.w * 100}%`, height: `${rect.h * 100}%` }}
        onPointerDown={(e) => begin('move', e)}
      >
        {(['nw', 'ne', 'sw', 'se'] as const).map((handle) => (
          <span key={handle} className={`crop-handle ${handle}`} onPointerDown={(e) => begin(handle, e)} />
        ))}
      </div>
    </div>
  )
}
